import { workspaceNotificationRulesQuery } from "@common/api/notification-rules"
import type { NotificationRuleDocument } from "@common/client"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { BellRing } from "lucide-react"
import { z } from "zod"
import { ListEmpty } from "@/components/layout/list-empty"
import { ListTabs } from "@/components/layout/list-tabs"
import { NameFilter } from "@/components/layout/name-filter"
import { PageContent, PageTitle } from "@/components/page/page-content"
import {
  type Names,
  phraseText,
} from "@/routes/_layout/settings/_panel/-lib/flow"
import { RuleCard } from "./-components/rule-card"
import { audienceItem } from "./-lib/audiences"
import { rulePhrases } from "./-lib/conditions"
import { AREAS, EVENT_AREAS, EVENTS, type EventArea } from "./-lib/events"
import { CREATE_RULES_PERMISSION } from "./-lib/permission"

const searchSchema = z.object({
  search: z.string().optional().catch(""),
  area: z.enum(EVENT_AREAS).optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/notification-rules/",
)({
  validateSearch: (search) => searchSchema.parse(search),
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(workspaceNotificationRulesQuery()),
  component: NotificationRulesPage,
})

function NotificationRulesPage(): React.ReactElement {
  const { search = "", area } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data } = useSuspenseQuery(workspaceNotificationRulesQuery())
  const canCreate = useHasPermission(CREATE_RULES_PERMISSION)
  const needle = search.toLowerCase()
  const matching = data.rules.filter((rule) =>
    searchText(rule, data.names).includes(needle),
  )
  const areas = EVENT_AREAS.filter((candidate) =>
    matching.some((rule) => EVENTS[rule.event_type].area === candidate),
  )
  const selected = areas.find((candidate) => candidate === area) ?? areas[0]
  const update = (next: { search?: string; area?: EventArea }) =>
    navigate({
      search: (previous) => ({ ...previous, ...next }),
      replace: true,
    })
  const filter = (next: string) => update({ search: next || undefined })

  return (
    <PageContent width="full">
      <PageTitle
        title="Notification rules"
        description={<Introduction />}
        action={
          <div className="flex shrink-0 items-center gap-2">
            <NameFilter
              search={search}
              onSearch={filter}
              placeholder="Filter rules…"
            />
            {canCreate && (
              <Button asChild size="sm">
                <Link to="/settings/workspace/notification-rules/create">
                  New rule
                </Link>
              </Button>
            )}
          </div>
        }
      />
      {selected && (
        <div className="overflow-x-auto">
          <ListTabs
            tabs={areas}
            tab={selected}
            label={(tab) => AREAS[tab].label}
            name="Area"
            onSelect={(next) => update({ area: next })}
          />
        </div>
      )}
      {selected ? (
        <div className="space-y-3">
          {matching
            .filter((rule) => EVENTS[rule.event_type].area === selected)
            .map((rule) => (
              <RuleCard
                key={rule.id ?? rule.name}
                rule={rule}
                names={data.names}
              />
            ))}
        </div>
      ) : (
        <ListEmpty
          icon={BellRing}
          title="No matching rules"
          body="Try another rule, event or person."
          onClear={() => filter("")}
        />
      )}
    </PageContent>
  )
}

function Introduction(): React.ReactElement {
  return (
    <>
      Each rule waits for something to happen, checks its conditions, then
      notifies the people it names. People only hear about records they can see,
      and only once each time. Everyone can mute most rules in their own{" "}
      <Link
        to="/settings/account/notifications"
        className="text-foreground underline"
      >
        notification settings
      </Link>
      .
    </>
  )
}

function searchText(rule: NotificationRuleDocument, names: Names): string {
  const event = EVENTS[rule.event_type]
  return [
    rule.name,
    event.label,
    ...rule.audiences.map((audience) => audienceItem(audience, names).label),
    ...rulePhrases(rule, names).map(phraseText),
  ]
    .join(" ")
    .toLowerCase()
}
